import { classNames } from "@circle-react/helpers/twMergeWithCN";
import type { Profile } from "@circle-react/types/CommunityBot/Profile";
import { Avatar } from "@circleco/compass/components/Avatar";
import { Icon } from "@circleco/compass/components/Icon";
import { useState } from "react";

import { t } from "@/i18n-js/instance";

import { AvatarListRow } from "./AvatarListRow";

const COLLAPSED_ROW_COUNT = 2;

interface AvatarsListProps {
  avatars: Profile[];
  onSelect: (avatar: Profile) => void;
}

export const AvatarsList = ({ avatars, onSelect }: AvatarsListProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const initialAvatars = avatars.slice(0, COLLAPSED_ROW_COUNT);
  const extraAvatars = avatars.slice(COLLAPSED_ROW_COUNT);
  const canToggle = extraAvatars.length > 0;
  const toggleLabel = isExpanded
    ? t("avatars.conversation_panel.less")
    : t("avatars.conversation_panel.see_all");
  const toggleAriaLabel = isExpanded
    ? t("avatars.conversation_panel.show_fewer_avatars")
    : t("avatars.conversation_panel.show_all_avatars");

  const renderAvatarRow = (avatar: Profile) => (
    <AvatarListRow
      key={avatar.id}
      label={avatar.name}
      prefix={<Avatar name={avatar.name} src={avatar.avatar_url} decorative />}
      onClick={() => onSelect(avatar)}
    />
  );

  return (
    <section
      aria-label={t("avatars.conversation_panel.avatars_title")}
      className="flex shrink-0 flex-col"
    >
      <div className="flex flex-col gap-2">
        {initialAvatars.map(renderAvatarRow)}
      </div>
      {canToggle && (
        <>
          <div
            className={classNames(
              "grid transition-[grid-template-rows,opacity] duration-300 ease-in-out motion-reduce:transition-none",
              isExpanded
                ? "grid-rows-[1fr] opacity-100"
                : "grid-rows-[0fr] opacity-0",
            )}
            inert={!isExpanded}
          >
            <div className="min-h-0 overflow-hidden">
              <div className="flex flex-col gap-2 pt-2">
                {extraAvatars.map(renderAvatarRow)}
              </div>
            </div>
          </div>
          <div className="pt-2">
            <AvatarListRow
              aria-expanded={isExpanded}
              aria-label={toggleAriaLabel}
              label={toggleLabel}
              prefix={
                <span className="flex shrink-0 items-center justify-center rounded-full bg-secondary p-2">
                  <Icon
                    name={isExpanded ? "chevron-top" : "chevron-down"}
                    size="sm"
                    color="primary"
                  />
                </span>
              }
              onClick={() => setIsExpanded(!isExpanded)}
            />
          </div>
        </>
      )}
    </section>
  );
};
